import React from "react";
import { LeveledFiltersConfig } from "@/components/data-table/data-table";
import { getDisplayName } from "@/config/datasources";
import { LEGISLATION_SOURCE_FILTER_KEY } from "@/pages/global-search/constants/searchFilters";
import { getFilterDisplayName } from "../../utils/entityInfoHelpers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

interface SearchResultsLegislationFiltersProps {
  legislationFilters: Record<string, string[]>;
  leveledFilters: LeveledFiltersConfig | undefined;
}
const SearchResultsLegislationFilters = ({
  legislationFilters,
  leveledFilters,
}: SearchResultsLegislationFiltersProps) => {
  const MAX_VISIBLE_FILTERS = 2; // Show max 2 filter badges before grouping

  const allFilters = Object.entries(legislationFilters)
    .filter(
      ([key, values]) =>
        values &&
        Array.isArray(values) &&
        values.length > 0 &&
        // Filter out date-related fields
        !["createdAt", "updatedAt", "filterDateField"].includes(key)
    )
    .map(([filterKey, filterValues]) => {
      // Special handling for source (datasource) filter
      if (filterKey === LEGISLATION_SOURCE_FILTER_KEY) {
        const displayValues = filterValues
          .map((source) => getDisplayName(source) ?? source)
          .join(", ");
        return {
          key: filterKey,
          displayName: "Kilde",
          displayValues,
          fullTooltip: displayValues,
          element: (
            <div key={filterKey} className="inline-flex shrink-0">
              <div
                className="px-1 py-0.5 bg-success/10 text-success text-[10px] rounded truncate"
                title={displayValues}
              >
                Kilde:{" "}
                {filterValues.length <= 1
                  ? displayValues
                  : filterValues
                      .slice(0, 1)
                      .map((source) => getDisplayName(source) ?? source)
                      .join(", ") + ` +${filterValues.length - 1} mere`}
              </div>
            </div>
          ),
        };
      }

      // Generic handling for other filters using leveledFilters display names
      const displayName = getFilterDisplayName(filterKey, leveledFilters);
      const displayValues = filterValues.join(", ");
      return {
        key: filterKey,
        displayName,
        displayValues,
        fullTooltip: `${displayName}: ${displayValues}`,
        element: (
          <div key={filterKey} className="inline-flex shrink-0">
            <div
              className="px-1 py-0.5 bg-destructive/10 text-destructive text-[10px] rounded truncate"
              title={displayValues}
            >
              {displayName}:{" "}
              {filterValues.length <= 1
                ? filterValues.join(", ")
                : filterValues.slice(0, 1).join(", ") +
                  ` +${filterValues.length - 1} mere`}
            </div>
          </div>
        ),
      };
    });

  if (allFilters.length === 0) return [];

  // If we have more filters than the max, show first few and group the rest
  if (allFilters.length > MAX_VISIBLE_FILTERS) {
    const visibleFilters = allFilters.slice(0, MAX_VISIBLE_FILTERS);
    const hiddenFilters = allFilters.slice(MAX_VISIBLE_FILTERS);

    // Count only values from hidden filter keys (not collapsed values within visible badges)
    const hiddenCount = hiddenFilters.reduce(
      (sum, f) => sum + (legislationFilters[f.key]?.length || 0),
      0
    );

    const elements: React.ReactElement[] = [];

    // Add bullet separator before first filter (after tab info)
    elements.push(
      <span key="sep-start" className="text-foreground mx-2 shrink-0">
        •
      </span>
    );

    // Add visible filters with separators
    visibleFilters.forEach((f, idx) => {
      if (idx > 0) {
        elements.push(
          <span key={`sep-${idx}`} className="text-foreground mx-2 shrink-0">
            •
          </span>
        );
      }
      elements.push(f.element);
    });

    // Only show "+X filtre" if there are hidden values
    if (hiddenCount > 0) {
      // Add separator before "more filters" tooltip
      elements.push(
        <span key="sep-more" className="text-foreground mx-2 shrink-0">
          •
        </span>
      );

      // Add "more filters" tooltip
      elements.push(
        <Tooltip key="more-filters">
          <TooltipTrigger asChild>
            <div className="inline-flex shrink-0">
              <div className="px-1 py-0.5 bg-primary/20 text-primary text-[10px] rounded cursor-help">
                +{hiddenCount} {hiddenCount === 1 ? "filter" : "filtre"}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="flex flex-col gap-1 text-[10px]">
              {hiddenFilters.map((f, idx) => (
                <div key={idx}>{f.fullTooltip}</div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return elements;
  }

  // For all visible filters, add separators between them
  const elements: React.ReactElement[] = [];

  // Add bullet separator before first filter (after tab info)
  elements.push(
    <span key="sep-start" className="text-foreground mx-2 shrink-0">
      •
    </span>
  );

  allFilters.forEach((f, idx) => {
    if (idx > 0) {
      elements.push(
        <span key={`sep-${idx}`} className="text-foreground mx-2 shrink-0">
          •
        </span>
      );
    }
    elements.push(f.element);
  });

  return elements;
};

export default SearchResultsLegislationFilters;
